import { message as wkMessage } from '@wk/wk-uni-lib';
import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosHeaders,
  AxiosError,
} from 'axios';
import qs from 'query-string';

import { REFRESH_TOKEN, TOKEN } from '@/constants';
import CacheStorage from '@/utils/cache-storage';
import { isServerSide } from '@/utils/env';
import { removeTokenInfo, setTokenInfo } from '@/utils/helpers';
import { getAPIBaseUrl } from '@/utils/url';

import { getLanguage } from '../utils/language';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipErrorHandler?: boolean;
    skipTransformResponse?: boolean;
    skipErrorAccess?: boolean;
  }
}

/**
 * 获取 Token
 */
const getToken = async (): Promise<string | undefined> => {
  if (isServerSide()) {
    // 服务端：从 cookies 获取
    try {
      const { cookies } = await import('next/headers');
      const cookieStore = await cookies();
      return cookieStore.get(TOKEN)?.value;
    } catch {
      return undefined;
    }
  } else {
    // 客户端：从 CacheStorage 获取
    return CacheStorage.getItem(TOKEN);
  }
};

/**
 * 获取 Refresh Token
 */
const getRefreshToken = async (): Promise<string | undefined> => {
  if (isServerSide()) {
    try {
      const { cookies } = await import('next/headers');
      const cookieStore = await cookies();
      return cookieStore.get(REFRESH_TOKEN)?.value;
    } catch {
      return undefined;
    }
  } else {
    return CacheStorage.getItem(REFRESH_TOKEN);
  }
};

/**
 * 获取 sign 参数
 */
const getSign = async (): Promise<string | undefined> => {
  if (isServerSide()) {
    try {
      const { headers } = await import('next/headers');
      const headersList = await headers();
      const url = headersList.get('referer') || '';
      if (url) {
        const urlObj = new URL(url);
        return urlObj.searchParams.get('sign') || undefined;
      }
    } catch {
      return undefined;
    }
  } else {
    const { sign } = qs.parse(globalThis.location?.search || '');
    return sign as string | undefined;
  }
  return undefined;
};

/**
 * 错误消息显示
 */
const showError = (message: string) => {
  if (isServerSide()) {
    throw new Error(message);
  } else {
    wkMessage.error(message);
  }
};

/**
 * 创建统一的 axios 实例
 */
const createUnifiedAxios = () => {
  const instance = axios.create({
    timeout: 30000,
    baseURL: getAPIBaseUrl(process.env.NEXT_PUBLIC_API_DOMAIN),
  });

  const requestHandler = async (requestConfig: InternalAxiosRequestConfig) => {
    // 确保 headers 存在
    if (!requestConfig.headers) {
      requestConfig.headers = new AxiosHeaders();
    }

    if (isServerSide()) {
      (requestConfig as any).metadata = { startTime: new Date().getTime() };
      // 服务端：异步获取
      const token = await getToken();
      requestConfig.headers.Authorization = token ? `Bearer ${token}` : '';

      const language = await getLanguage();
      requestConfig.headers['Accept-Language'] =
        language === 'zh' ? 'zh-CN' : 'en';

      const sign = await getSign();
      if (sign) {
        requestConfig.headers.sign = encodeURIComponent(sign);
      }
    } else {
      // 客户端：同步获取（恢复原来的逻辑）
      const { sign } = qs.parse(globalThis.location?.search || '');
      const token = CacheStorage.getItem(TOKEN);

      requestConfig.headers.Authorization = token ? `Bearer ${token}` : '';
      requestConfig.headers['Accept-Language'] =
        globalThis?.location?.href?.includes?.('/zh/') ? 'zh-CN' : 'en';

      if (sign) {
        requestConfig.headers.sign = encodeURIComponent(sign as string);
      }
    }

    // 设置平台标识
    requestConfig.headers.platform = 'web';

    return requestConfig;
  };

  const responseHandler = (res: AxiosResponse) => {
    const { config, status } = res || {};
    const { baseURL, url, method, data, headers, skipTransformResponse } =
      config || {};

    /* 请求日志，在vercel可以查看 */
    if (isServerSide()) {
      const endTime = new Date().getTime();
      const startTime = (res?.config as any)?.metadata?.startTime || endTime;
      console.log({
        url: `${baseURL}${url?.startsWith('/') ? '' : '/'}${url}`,
        method,
        ...(method === 'get' ? {} : { requestData: JSON.stringify(data) }),
        requestHeaders: JSON.stringify(headers),
        responseStatus: status,
        responseTime: `${endTime - startTime}ms`,
        responseHeaders: JSON.stringify(res.headers),
        responseData: JSON.stringify(res.data),
      });
    }

    if (skipTransformResponse) {
      return res;
    }

    return res.data;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTokenRefresh = async (
    status: number,
    config: InternalAxiosRequestConfig,
    isEn: boolean,
  ) => {
    try {
      const refreshToken = await getRefreshToken();

      if (!refreshToken) {
        throw new Error(
          isEn ? 'No refresh token available' : '没有可用的刷新Token',
        );
      }

      const url =
        +status === 406
          ? '/api/authorized/refresh_token'
          : '/api/authorized/refresh_token_V2';

      const requestData = +status === 406 ? undefined : { refreshToken };

      const response = await instance.post<
        { content: { token: string; refreshToken: string } },
        { content: { token: string; refreshToken: string } }
      >(url, requestData);

      const { token, refreshToken: newRefreshToken = '' } =
        response?.content || {};

      if (isServerSide()) {
        // 服务端：重新发起请求
        if (config && token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
          return instance.request(config);
        }
      } else {
        // 客户端：设置token并刷新页面
        setTokenInfo(token, newRefreshToken);
        window.location.reload();
      }
    } catch {
      if (isServerSide()) {
        throw new Error('Token refresh failed');
      } else {
        removeTokenInfo(null, true);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorHandler = async (err: AxiosError) => {
    const { baseURL, url, method, headers } = err.config || {};

    /* 错误日志，在vercel可以查看 */
    if (isServerSide()) {
      console.error({
        url: `${baseURL}${url?.startsWith('/') ? '' : '/'}${url}`,
        method,
        ...(method === 'get'
          ? {}
          : { requestData: JSON.stringify(err?.config?.data || {}) }),
        requestHeaders: JSON.stringify(headers),
        errStatus: err.status,
        errMessage: err.message,
        errorCode: err.code,
        ...(err.response
          ? {
              stuErrData: JSON.stringify(err?.response?.data || {}),
            }
          : {}),
      });
    }

    const isEn = (await getLanguage()) === 'en';

    // 网络错误
    if (err?.code === 'ERR_NETWORK') {
      showError(
        isEn
          ? 'Network error. Please try again later.'
          : '网络异常，请稍后再试',
      );
      return;
    }

    if (!err.response) {
      throw err;
    }

    const { status, config, data } = err.response;
    const { errors, message } =
      (data as {
        errors: Record<string, string>;
        message: string;
      }) || {};
    const errMsg = (errors && errors[Object.keys(errors)[0]]) || message;

    // 维护中状态 (433)
    if (+status === 433) {
      if (isServerSide()) {
        throw new Error(
          isEn ? 'Service is under maintenance' : '服务正在维护中',
        );
      } else {
        window.location.href = '/notice/now';
      }
      return;
    }

    // 未授权状态 (401)
    if (+status === 401 && !config?.skipErrorAccess) {
      if (isServerSide()) {
        throw new Error(isEn ? 'Unauthorized access' : '未授权访问');
      } else {
        removeTokenInfo(null, false);
      }
      return;
    }

    // Token 刷新相关错误 (406, 403)
    if (+status === 406 || +status === 403) {
      if (isServerSide()) {
        await handleTokenRefresh(status, config, isEn);
      } else {
        // 客户端：恢复原来的token刷新逻辑
        const url =
          +status === 406
            ? '/api/authorized/refresh_token'
            : '/api/authorized/refresh_token_V2';
        const requestData =
          +status === 406
            ? undefined
            : {
                refreshToken: CacheStorage.getItem(REFRESH_TOKEN),
              };
        instance
          .post<
            { content: { token: string; refreshToken: string } },
            { content: { token: string; refreshToken: string } }
          >(url, requestData)
          .then(res => {
            const { token, refreshToken = '' } = res?.content || {};
            setTokenInfo(token, refreshToken);
            window.location.reload();
          })
          .catch(() => {
            removeTokenInfo(null, true);
          });
      }
      return;
    }

    // 验证错误 (422)
    if (+status === 422 && !config?.skipErrorHandler) {
      const clearError =
        errMsg || (isEn ? 'Unknown validation error' : '未知错误');
      showError(clearError);
      return;
    }

    // 跳过错误处理
    if (config.skipErrorHandler) {
      throw err;
    }

    // 其他错误
    if (typeof data === 'string' || errMsg) {
      showError(errMsg || (isEn ? 'Unknown error' : '未知错误'));
    }

    throw err;
  };

  // 设置拦截器
  instance.interceptors.request.use(requestHandler);
  instance.interceptors.response.use(responseHandler, errorHandler);

  return instance;
};

// 创建单例实例
const apiClient = createUnifiedAxios();

export default apiClient;

// 导出便捷方法，保持向后兼容
export const { get, post, put, delete: del, patch, request } = apiClient;
